// @owners { team: patients }
import {
  Body,
  Button,
  Column,
  ExternalLink,
  H2,
  H3,
  InputText,
  LgSpacing,
  Row,
  Toast,
  ToastContext,
  XsSpacing,
  XxlSpacing,
} from '@alto/design-system';
import { useRegisterWaitlistUser } from '@alto/features';
import { useNavigation } from '@alto/navigation';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { setFertDeliverySignup } from '~shared/actions/uiOnboarding';
import location from '~shared/assets/images/box_with_wings@3x.png';
import { ALTO_LOCATIONS_URL, PRIVACY_POLICY_URL } from '~shared/constants';
import { useAnalytics } from '~shared/hooks/useAnalytics';
import { EVENTS } from '~shared/lib/analytics/src/constants';
import { useDispatchShared } from '~shared/store';
import { validateEmail } from '~shared/validations';
import { Image } from '~web/components/Image';

type Props = {
  readonly zip: string;
};

export const OnboardingGeoUncovered = ({ zip }: Props) => {
  const dispatch = useDispatchShared();
  const { trackPageView, trackEvent } = useAnalytics();
  const { navigate } = useNavigation();
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const { addToast } = useContext(ToastContext);
  const { mutateAsync } = useRegisterWaitlistUser();

  useEffect(() => {
    trackPageView({
      event: EVENTS.ONBOARDING_OOZ_ZIP_CODE,
      params: {
        zip,
      },
    });
  }, [zip, trackPageView]);

  const handleCreateAccountFert = useCallback(() => {
    trackEvent({
      event: 'Onboarding Fertility Delivery Sign Up Clicked',
      params: {
        zip,
      },
    });
    dispatch(setFertDeliverySignup(false));
  }, [dispatch, trackEvent, zip]);

  const handleSubmit = useCallback(async () => {
    if (validateEmail(email)) {
      setError('Please enter a valid email address');
      return;
    }
    setError('');
    const response = await mutateAsync({ zip, email, first_name: null, last_name: null });
    if (response.errors) {
      addToast(<Toast variant="error">Error saving email address</Toast>);
      return;
    }
    addToast(<Toast variant="success">Email address saved</Toast>);
    navigate('RouteLogin');
  }, [addToast, email, mutateAsync, navigate, zip]);

  const handleEmailChange = (text: string) => {
    setEmail(text);
    if (error && !validateEmail(text)) {
      setError('');
    }
  };

  return (
    <Column gap="SM">
      <Image
        center
        src={location}
        alt="Coming Soon"
      />
      <Column center>
        <H2 center>Limited Prescription Support</H2>
        <XsSpacing />
        <Body center>
          We offer mail delivery in zip code <Body fontFamily="bold">{zip}</Body> for select medications and programs,
          including fertility care. To see if your location would qualify with eligible medications, create an account
          below.
        </Body>
        <LgSpacing />
        <Button
          width="full"
          onPress={handleCreateAccountFert}
        >
          Create account
        </Button>
        <XxlSpacing />
        <H3>Have other pharmacy needs?</H3>
        <XsSpacing />
        <Body center>Enter your email address and we'll notify you when we arrive in your area.</Body>
      </Column>
      <InputText
        onChangeText={handleEmailChange}
        placeholder="Enter email address"
        error={error}
      />
      <Button
        width="full"
        type="secondary"
        onPress={handleSubmit}
      >
        Submit
      </Button>
      <Row center>
        <ExternalLink href={ALTO_LOCATIONS_URL}>See Alto's current locations</ExternalLink>
      </Row>
      <Row center>
        <ExternalLink href={PRIVACY_POLICY_URL}>Privacy Policy</ExternalLink>
      </Row>
    </Column>
  );
};
